import React, { useState, useEffect, useMemo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import Button from "components/lib/Shared/Button";
import _get from "lodash/get";
import _merge from "lodash/merge";
import { Formik, Field, Form } from "formik";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import DatePicker from "react-datepicker";
import { useParams } from "react-router-dom";
import Tabs from "components/lib/Shared/Tabs";
import getInitialValues from "../../Appointment/CompanyOfficialsTab/AssembledCompanyOfficialForms/CompanyOfficialsAuthorizedDirector/getInitialValues";
import { getDocumentData } from "../../Appointment/CompanyOfficialsTab/FileUploadForm/documentData";
import { getOfficial, resetOfficial } from "state/slices/companyDashboard";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import DocumentsUpload from "./documentsUpload";
import { DocumentAccordion } from "./documentsUpload";
import { validationSchema } from "./ValidationSchema";
import { handleFilesUpload } from "utils/filesUtils";
import { uploadTemplateFileAsync } from "state/slices/templateFile";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { Constants } from "config/constants";
import * as Yup from "yup";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import AppointmentTypeForm from "../../Appointment/CompanyOfficialsTab/CompanyOfficialSelection/AppointmentTypeForm";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";

const initialTabs = [
  { id: "generalInformation", label: "General information", disabled: true },
  { id: "uploadDocuments", label: "Change Documents", disabled: true },
];

const initialValuesTemplate = {
  role: [],
  names: "",
  postalAddress: {
    postalAddress: "",
    postalCode: "",
    serviceAddress: "N/A",
  },
  residentialAddressDetails: "",
  letterOfResignation: {
    modeType: "",
    subModeType: "",
    getLetterOfResignationName: "",
  },
  shareholdersSpecialNotice: {
    modeType: "",
    subModeType: "",
    getShareholdersSpecialNoticeName: "",
  },
  deathCertificate: {
    modeType: "",
    subModeType: "",
    getDeathCertificateName: "",
  },
  insolvencyOrder: {
    modeType: "",
    subModeType: "",
    getInsolvencyOrderName: "",
  },
  medicalReport: {
    modeType: "",
    subModeType: "",
    getMedicalReportName: "",
  },
  courtOrder: {
    modeType: "",
    subModeType: "",
    getCourtOrderName: "",
  },
  statutoryDeclaration: {
    modeType: "",
    subModeType: "platform-resolution-type",
    getStatutoryDeclarationName: "",
  },
  noticeOfRemoval: {
    modeType: "",
    subModeType: "",
    getNoticeOfRemovalName: "",
  },
  approvalType: "",
};

const CessationSubRowForDelete = ({
  row,
  visibleColumns,
  isInTransactionEditContext = false,
  selectType,
  currentTransaction = null,
  isChangeList,
  setChangeList = () => null,
  setPayload = () => null,
  setTrxId = () => null,
  onRemoveOfficial,
  isEditMode = false,
}) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(initialTabs[0]);
  const [officialId, setOfficialId] = useState("");
  const [cessationDate, setCessationDate] = useState(() => {
    // Prepopulate date from current transaction or original row
    if (isEditMode) {
      if (currentTransaction?.cessationDate) {
        return new Date(currentTransaction.cessationDate);
      } else if (row.original?.cessationDate) {
        return new Date(row.original.cessationDate);
      }
    }
    return null;
  });

  // Initialize form values
  const [initialValues, setInitialValues] = useState(() => {
    const baseValues = {
      ...initialValuesTemplate,
      ...row.original,
      cessationDate: cessationDate || null,
      approvalType: "boardApproval", // We use this in updateCompanyDetailsContext
    };

    // Add uploaded documents if in edit mode
    if (isEditMode && currentTransaction) {
      return {
        ...baseValues,
        ...(currentTransaction.resignationLetter && {
          letterOfResignation: {
            ...initialValuesTemplate.letterOfResignation,
            uploaded: Array.isArray(currentTransaction.resignationLetter)
              ? currentTransaction.resignationLetter
              : [currentTransaction.resignationLetter],
          },
        }),
        ...(currentTransaction.statutoryDeclaration && {
          statutoryDeclaration: {
            ...initialValuesTemplate.statutoryDeclaration,
            uploaded: Array.isArray(currentTransaction.statutoryDeclaration)
              ? currentTransaction.statutoryDeclaration
              : [currentTransaction.statutoryDeclaration],
          },
        }),
        // Add other document fields similarly...
      };
    }
    return baseValues;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast(5000);
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();

  const { official, getOfficialStatus } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const { batchNo } = useSelector((state) => state.batchNumberSlice);
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const propertyExistOrNull = JSON.parse(
    localStorage.getItem("initiateTransactionObj")
  );

  const isRetirementOrRotation =
    propertyExistOrNull?.selectType === "operation" &&
    (propertyExistOrNull?.reason === "retirement" ||
      propertyExistOrNull?.reason === "rotation");

  const documentData = useMemo(() => {
    return getDocumentData(row?.original?.idType);
  }, [row?.original?.idType]);

  const filteredTabs = useMemo(() => {
    return initialTabs.filter((tab) => {
      if (tab.id === "uploadDocuments") {
        const isSpecialRole = [
          "Company Secretary",
          "Auditor",
          "Administrator",
          "Authorized Person",
        ].includes(propertyExistOrNull?.applicationType);
        const isAlternateDirector =
          propertyExistOrNull?.applicationType === "Alternate Director";

        if (isSpecialRole && propertyExistOrNull?.reason === "removal") {
          return false;
        }

        if (isSpecialRole && propertyExistOrNull?.reason === "resignation") {
          return true;
        }

        if (isAlternateDirector && propertyExistOrNull?.reason === "removal") {
          return true;
        }

        if (isRetirementOrRotation) {
          return false;
        }

        return (
          propertyExistOrNull?.reason === "resignation" ||
          propertyExistOrNull?.reason === "removal" ||
          propertyExistOrNull?.reason === "death" ||
          propertyExistOrNull?.reason === "bankruptcy" ||
          propertyExistOrNull?.reason === "insanity" ||
          propertyExistOrNull?.reason === "courtOrder"
        );
      }
      return true;
    });
  }, [propertyExistOrNull, isRetirementOrRotation]);

  /** Used in updateCompanyDetails component */
  const {
    isInUpdateCompanyDetailsContext,
    setCreatedTransactionsIds,
    createdTransactionsIds,
    transactionToEdit,
  } = useContext(UpdateCompanyDetailsContext);

  const goToPrevStep = () => {
    if (selectedTabIndex === 0) return;
    setSelectedTabIndex(selectedTabIndex - 1);
  };

  const goToNextStep = (values) => {
    if (selectedTabIndex < filteredTabs.length - 1) {
      setCessationDate(values.cessationDate);
      setSelectedTabIndex(selectedTabIndex + 1);
    }
  };

  useEffect(() => {
    if (row?.original?._id) {
      dispatch(
        getOfficial({
          companyId: params.companyId,
          officialId: row?.original?._id,
          queryParams: {
            idType: row?.original?.idType,
            JSGroupID: row?.original?.JSGroupID,
          }
        })
      );
      setOfficialId(row?.original?._id);
    }

    return () => {
      dispatch(resetOfficial());
    };
  }, [row?.original?._id, dispatch, params.companyId]);

  useEffect(() => {
    const currentTab = filteredTabs[selectedTabIndex];
    if (currentTab) setSelectedTab(currentTab);
  }, [selectedTabIndex, filteredTabs]);

  useEffect(() => {
    if (official) {
      if (isEditMode && row?.original) {
        setInitialValues((prevValues) =>
          _merge({}, initialValuesTemplate, row.original)
        );
      } else {
        setInitialValues((prevValues) =>
          _merge({}, initialValuesTemplate, {
            ...official,
            names: official.names || "",
            postalAddress: official.postalAddress || {
              postalAddress: "",
              postalCode: "",
              serviceAddress: "N/A",
            },
            residentialAddressDetails: official.residentialAddressDetails || "",
          })
        );
      }
    }
  }, [official, isEditMode, row]);

  const mergedInitialValues = useMemo(() => {
    const baseValues = {
      ...initialValuesTemplate,
      ...row.original,
      cessationDate: cessationDate || null,
    };

    if (isEditMode && currentTransaction) {
      return {
        ...baseValues,
        ...(currentTransaction.resignationLetter && {
          letterOfResignation: {
            ...initialValuesTemplate.letterOfResignation,
            uploaded: Array.isArray(currentTransaction.resignationLetter)
              ? currentTransaction.resignationLetter
              : [currentTransaction.resignationLetter],
          },
        }),
        ...(currentTransaction.statutoryDeclaration && {
          statutoryDeclaration: {
            ...initialValuesTemplate.statutoryDeclaration,
            uploaded: Array.isArray(currentTransaction.statutoryDeclaration)
              ? currentTransaction.statutoryDeclaration
              : [currentTransaction.statutoryDeclaration],
          },
        }),
        // Add other document fields similarly...
      };
    }
    return _merge(
      {},
      getInitialValues({
        idType: row?.original?.idType,
        isNonResident: row?.original?.isNonResident,
        documentData,
      }),
      initialValuesTemplate
    );
  }, [
    row?.original,
    row?.original?.idType,
    row?.original?.isNonResident,
    documentData,
    isEditMode,
    currentTransaction,
    cessationDate,
  ]);

  useEffect(() => {
    setInitialValues(mergedInitialValues);
  }, [setInitialValues, mergedInitialValues]);

  const handleCessationSubmit = async (formValues) => {
    const reason = propertyExistOrNull?.reason;
    const uploads = {};
    const fileUploads = [];

    if (!formValues.cessationDate) {
      toast("error", "Cessation date is required");
      return;
    }

    try {
      if (!isRetirementOrRotation) {
        const pushUpload = async (fieldName, fileTypeConst) => {
          const fieldValue = formValues[fieldName];
          if (isInUpdateCompanyDetailsContext) {
            fieldValue.modeType = "pre-signed-documents";
            fieldValue.subModeType = "self-resolution-type";
          }
          if (!fieldValue || !fieldValue.modeType) {
            throw new Error(`Mode type is required for ${fieldName}`);
          }
          const uploadedFile = await handleFilesUpload(fieldValue);

          const response = await dispatch(
            uploadTemplateFileAsync({
              data: {
                company: params.companyId,
                official: officialId,
                fileType: fileTypeConst,
                fileName:
                  uploadedFile?.fileName ||
                  uploadedFile?.name ||
                  `${fieldName}.pdf`,
                modeType: fieldValue.modeType,
                subModeType: fieldValue.subModeType,
              },
            })
          ).unwrap();

          /**
           * We update official change documents
           * Change documents belong to individual or corporate official but not a transaction
           *
           * */
          const updateOfficialResponse = await dispatch(
            updateCompanyOfficialAsync({
              officialId,
              data: {
                cessationDate: formValues.cessationDate,
                companyId: params.companyId,
                changeType: Constants.TRANSACTION_CHANGE_TYPES.CESSATION,
                idType: row.original?.idType, // pass the idType to determine if is corporate or individual
                uploadChangeDocuments: {
                  ...row.original?.uploadChangeDocuments,
                  cessation: {
                    ...row.original?.uploadChangeDocuments?.cessation,
                    [fieldName]: {
                      templateFile: response.document._id,
                    },
                  },
                },
              },
            })
          );

          const uploadKey =
            fieldName === "letterOfResignation"
              ? "resignationLetter"
              : fieldName;
          uploads[uploadKey] = response.document._id;
          fileUploads.push({ field: fieldName, response });
        };

        switch (reason) {
          case "resignation":
            await pushUpload(
              "letterOfResignation",
              Constants.Template_FILE_TYPES.RESIGNATION_LETTER
            );
            if (
              row.original.designation === "Director" ||
              row.original.designation === "Alternate Director" ||
              propertyExistOrNull?.applicationType === "Director" ||
              propertyExistOrNull?.applicationType === "Alternate Director"
            ) {
              await pushUpload(
                "statutoryDeclaration",
                Constants.Template_FILE_TYPES.STATUTORY_DECLARATION
              );
            }
            break;
          case "removal":
            if (
              row.original.designation === "Alternate Director" ||
              propertyExistOrNull?.applicationType === "Alternate Director"
            ) {
              await pushUpload(
                "noticeOfRemoval",
                Constants.Template_FILE_TYPES.NOTICE_OF_REMOVAL_BY_APPOINTOR
              );
            } else if (
              ![
                "Company Secretary",
                "Auditor",
                "Administrator",
                "Authorized Person",
              ].includes(propertyExistOrNull?.applicationType)
            ) {
              await pushUpload(
                "shareholdersSpecialNotice",
                Constants.Template_FILE_TYPES.SHAREHOLDERS_SPECIAL_NOTICE
              );
            }
            break;
          case "death":
            await pushUpload(
              "deathCertificate",
              Constants.Template_FILE_TYPES.DEATH_CERTIFICATE
            );
            break;
          case "bankruptcy":
            await pushUpload(
              "insolvencyOrder",
              Constants.Template_FILE_TYPES.INSOLVENCY_ORDER
            );
            break;
          case "insanity":
            await pushUpload(
              "medicalReport",
              Constants.Template_FILE_TYPES.MEDICAL_REPORT
            );
            break;
          case "courtOrder":
            await pushUpload(
              "courtOrder",
              Constants.Template_FILE_TYPES.COURT_ORDER
            );
            break;
          default:
            break;
        }
      }

      // Use idType from formValues if available, else from row.original
      const idType = formValues.idType || row.original.idType;
      // Determine targetKey based on idType
      let targetKey;
      if (
        ["Kenyan Citizen", "Foreign Resident", "Foreigner"].includes(idType)
      ) {
        targetKey = "individualOfficials";
      } else if (
        ["Local Company", "Foreign Company", "Other e.g Parastatals"].includes(
          idType
        )
      ) {
        targetKey = "corporateOfficials";
      } else {
        targetKey = "individualOfficials"; // fallback to individualOfficials
      }

      const formDetails = {
        companyId: params.companyId,
        changeType: Constants.TRANSACTION_CHANGE_TYPES.CESSATION,
        processType: reason,
        idType: row.original.idType,
        status: Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT,
        officialId: officialId,
        applicationType: propertyExistOrNull?.applicationType,
        approvalType: isInUpdateCompanyDetailsContext
          ? formValues.approvalType
            ? formValues.approvalType
            : "boardApproval"
          : propertyExistOrNull?.approvalType || "boardApproval",
        isReviewed: false,
        isReviewInThisStep: true,
        ...(batchNo && { batchNo: batchNo }),
        transactionType: isInUpdateCompanyDetailsContext
          ? Constants.TRANSACTION_TYPES.MANUAL
          : Constants.TRANSACTION_TYPES.AUTOMATIC,
      };

      let transactionResponse;
      let wasUpdate = false;

      if (
        isEditMode ||
        transactionToEdit?.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CESSATION
      ) {
        if (!params.trxId) {
          toast("error", "Transaction ID is missing in edit mode");
          return;
        }

        const existingTransaction = initiatedTransactions?.items?.find(
          (t) => t._id === params.trxId
        );
        if (!existingTransaction) {
          toast("error", "Transaction not found for update");
          return;
        }

        wasUpdate = true;
        const updatedOfficials = Array.isArray(existingTransaction[targetKey])
          ? [...existingTransaction[targetKey], officialId]
          : [officialId];

        const updatedTransaction = {
          ...existingTransaction,
          [targetKey]: updatedOfficials,
          ...(batchNo && { batchNo: batchNo }),
        };

        Object.entries(uploads).forEach(([key, value]) => {
          const fieldName =
            key === "resignationLetter" ? "resignationLetter" : key;
          const existingDocs = existingTransaction[fieldName] || [];
          if (!existingDocs.includes(value)) {
            updatedTransaction[fieldName] = [...existingDocs, value];
          } else {
            updatedTransaction[fieldName] = existingDocs;
          }
        });

        const updatePayload = {
          ...formDetails,
          documentId: params.trxId,
          [targetKey]: updatedOfficials,
        };

        Object.entries(updatedTransaction).forEach(([key, value]) => {
          if (
            Array.isArray(value) &&
            [
              "resignationLetter",
              "statutoryDeclaration",
              "shareholdersSpecialNotice",
              "deathCertificate",
              "insolvencyOrder",
              "medicalReport",
              "noticeOfRemoval",
              "courtOrder",
            ].includes(key)
          ) {
            updatePayload[key] = value;
          }
        });

        transactionResponse = await handleCompanyMaintenanceFormData(
          "existing-request",
          updatePayload,
          params.companyId
        );

        if (transactionResponse?.payload?.status === true) {
          const updatedInitiateTransactionObj = {
            ...propertyExistOrNull,
            transaction: updatedTransaction,
          };
          localStorage.setItem(
            "initiateTransactionObj",
            JSON.stringify(updatedInitiateTransactionObj)
          );
        }
      } else {
        const existingTransaction =
          isInTransactionEditContext === false && params.trxId
            ? initiatedTransactions?.items?.find((t) => t._id === params.trxId)
            : propertyExistOrNull?.transaction;

        if (existingTransaction) {
          wasUpdate = true;
          const updatedOfficialsNonEdit = Array.isArray(
            existingTransaction[targetKey]
          )
            ? [...existingTransaction[targetKey], officialId]
            : [officialId];

          const updatedTransaction = {
            ...existingTransaction,
            [targetKey]: updatedOfficialsNonEdit,
            ...(batchNo && { batchNo: batchNo }),
          };

          Object.entries(uploads).forEach(([key, value]) => {
            const fieldName =
              key === "resignationLetter" ? "resignationLetter" : key;
            const existingDocs = existingTransaction[fieldName] || [];
            if (!existingDocs.includes(value)) {
              updatedTransaction[fieldName] = [...existingDocs, value];
            } else {
              updatedTransaction[fieldName] = existingDocs;
            }
          });

          const updatePayload = {
            ...formDetails,
            documentId: existingTransaction._id,
            [targetKey]: updatedOfficialsNonEdit,
          };

          Object.entries(updatedTransaction).forEach(([key, value]) => {
            if (
              Array.isArray(value) &&
              [
                "resignationLetter",
                "statutoryDeclaration",
                "shareholdersSpecialNotice",
                "deathCertificate",
                "insolvencyOrder",
                "medicalReport",
                "noticeOfRemoval",
                "courtOrder",
              ].includes(key)
            ) {
              updatePayload[key] = value;
            }
          });

          transactionResponse = await handleCompanyMaintenanceFormData(
            "existing-request",
            updatePayload,
            params.companyId
          );

          if (transactionResponse?.payload?.status === true) {
            const updatedInitiateTransactionObj = {
              ...propertyExistOrNull,
              transaction: updatedTransaction,
            };
            localStorage.setItem(
              "initiateTransactionObj",
              JSON.stringify(updatedInitiateTransactionObj)
            );
          }
        } else {
          wasUpdate = false;
          const newFormDetails = {
            ...formDetails,
            ...Object.fromEntries(
              Object.entries(uploads).map(([key, value]) => [
                key === "resignationLetter" ? "resignationLetter" : key,
                [value],
              ])
            ),
          };

          transactionResponse = await handleCompanyMaintenanceFormData(
            "new-request",
            newFormDetails,
            params.companyId
          );

          if (transactionResponse?.payload?.status === true) {
            const newTransaction =
              transactionResponse?.payload?.savedTransaction;
            const updatedInitiateTransactionObj = {
              ...propertyExistOrNull,
              transaction: newTransaction,
            };
            if (isInUpdateCompanyDetailsContext) {
              setCreatedTransactionsIds([
                ...createdTransactionsIds,
                newTransaction?._id,
              ]);
            }
            localStorage.setItem(
              "initiateTransactionObj",
              JSON.stringify(updatedInitiateTransactionObj)
            );
          }
        }
      }

      if (transactionResponse?.payload?.status === true) {
        const trxId = isEditMode
          ? transactionResponse?.payload?.params?.trxId || params.trxId
          : transactionResponse?.payload?.savedTransaction?._id;
        setTrxId(trxId);
        toast(
          "success",
          wasUpdate
            ? "Transaction updated successfully"
            : "Cessation transaction created successfully"
        );

        row.toggleRowExpanded();

        if (!isInUpdateCompanyDetailsContext) {
          const updatedOfficial = {
            ...row.original,
            cessationDate: formValues.cessationDate,
            dateOfCeasingToBeADirector: formValues.cessationDate,
          };

          setChangeList((prevChangeList) => [
            ...prevChangeList,
            updatedOfficial,
          ]);
          setPayload((previousData) => [...previousData, formDetails]);
        }
      } else {
        toast(
          "error",
          wasUpdate
            ? "Failed to update transaction"
            : "Failed to create cessation transaction"
        );
      }
    } catch (error) {
      console.error("Transaction Error:", error);
      toast("error", error.message || "Failed to process transaction");
      throw error;
    }
  };

  if (!official || getOfficialStatus.status === "loading") {
    return (
      <tr className="bg-[#EDF0f5]">
        <td colSpan={visibleColumns.length} className="px-9">
          <div className="flex justify-center mx-auto m-2">
            <SpinnerIcon className="text-gray-400" />
          </div>
        </td>
      </tr>
    );
  }

  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = filteredTabs.length - 1 === selectedTabIndex;

  return (
    <>
      <tr className="bg-white">
        <td colSpan={12} className={`px-9`}>
          <Formik
            initialValues={{
              role: [],
              cessationDate: cessationDate || null,
              ...initialValues,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              if (!values.cessationDate) {
                toast("error", "Cessation date is required");
                setSubmitting(false);
                return;
              }

              if (isRetirementOrRotation && isFirstStep) {
                try {
                  await handleCessationSubmit(values);
                } catch (error) {
                  if (error.name === "ValidationError") {
                    const errorMessage = Object.values(error.errors)
                      .map((err) => err.message)
                      .join(", ");
                    toast("error", `Validation failed: ${errorMessage}`);
                  } else if (error.message) {
                    toast("error", error.message);
                  } else {
                    toast("error", "Failed to process transaction");
                  }
                } finally {
                  setSubmitting(false);
                }
              } else if (!isLastStep) {
                goToNextStep(values);
                setSubmitting(false);
              } else {
                try {
                  await handleCessationSubmit(values);
                } catch (error) {
                  if (error.name === "ValidationError") {
                    const errorMessage = Object.values(error.errors)
                      .map((err) => err.message)
                      .join(", ");
                    toast("error", `Validation failed: ${errorMessage}`);
                  } else if (error.message) {
                    toast("error", error.message);
                  } else {
                    toast("error", "Failed to process transaction");
                  }
                } finally {
                  setSubmitting(false);
                }
              }
            }}
          >
            {({ values, handleSubmit, setFieldValue }) => {
              const submitForm = async (e) => {
                e.preventDefault();
                if (!values.cessationDate) {
                  toast("error", "Cessation date is required");
                  return;
                }
                if (isRetirementOrRotation && isFirstStep) {
                  try {
                    setIsSubmitting(true);
                    await handleCessationSubmit(values);
                  } catch (error) {
                    console.error(error);
                  } finally {
                    setIsSubmitting(false);
                  }
                } else if (isLastStep) {
                  try {
                    setIsSubmitting(true);
                    await handleCessationSubmit(values);
                  } catch (error) {
                    console.error(error);
                  } finally {
                    setIsSubmitting(false);
                  }
                } else {
                  goToNextStep(values);
                }
              };

              return (
                <Form onSubmit={submitForm}>
                  {isInUpdateCompanyDetailsContext &&
                    propertyExistOrNull?.reason === "resignation" &&
                    row.original.designation === "Director" && (
                      <AppointmentTypeForm
                        setFieldValue={setFieldValue}
                        approvalType={values.approvalType}
                        designation={official?.designation}
                      />
                    )}

                  {((isInUpdateCompanyDetailsContext &&
                    values.approvalType &&
                    propertyExistOrNull?.reason === "resignation" &&
                    row.original.designation === "Director") ||
                    (isInUpdateCompanyDetailsContext &&
                      propertyExistOrNull?.reason === "resignation" &&
                      row.original.designation !== "Director") ||
                    (isInUpdateCompanyDetailsContext &&
                      propertyExistOrNull?.reason !== "resignation") ||
                    !isInUpdateCompanyDetailsContext) && (
                      <div className="relative">
                        <Tabs
                          tabs={filteredTabs}
                          selectedTabIndex={selectedTabIndex}
                          setSelectedTabIndex={setSelectedTabIndex}
                        />
                        {selectedTab?.id === "generalInformation" && (
                          <div className="grid grid-cols-6 gap-6">
                            <div className="py-6 col-span-6 sm:col-span-3 flex flex-col space-y-2">
                              <p className="block text-sm font-medium text-gray-700">
                                Date of Ceasing to be a {row.original.designation}
                              </p>
                              <Field name="cessationDate">
                                {({ field, form }) => (
                                  <>
                                    <DatePicker
                                      selected={field.value}
                                      onChange={(date) => {
                                        form.setFieldValue(field.name, date);
                                        setCessationDate(date);
                                      }}
                                      placeholderText="Enter date of cessation"
                                      className="border border-gray-300 rounded px-3 py-2 mt-1 focus:outline-none focus:border-blue-500"
                                    />
                                    {form.errors.cessationDate &&
                                      form.touched.cessationDate && (
                                        <div className="text-red-500">
                                          {form.errors.cessationDate}
                                        </div>
                                      )}
                                  </>
                                )}
                              </Field>
                            </div>
                          </div>
                        )}
                        {selectedTab?.id === "uploadDocuments" && (
                          <div className="mt-4">
                            {(() => {
                              const reason = propertyExistOrNull?.reason;
                              const isSpecialRole = [
                                "Company Secretary",
                                "Auditor",
                                "Administrator",
                                "Authorized Person",
                              ].includes(propertyExistOrNull?.applicationType);
                              const isAlternateDirector =
                                row.original.designation ===
                                "Alternate Director" ||
                                propertyExistOrNull?.applicationType ===
                                "Alternate Director";

                              if (
                                reason === "resignation" &&
                                (row.original.designation === "Director" ||
                                  row.original.designation ===
                                  "Alternate Director")
                              ) {
                                return (
                                  <DocumentAccordion
                                    reason={reason}
                                    isSpecialRole={isSpecialRole}
                                    isAlternateDirector={isAlternateDirector}
                                    row={row}
                                  />
                                );
                              }
                              switch (reason) {
                                case "resignation":
                                  return (
                                    <>
                                      <Field
                                        name="letterOfResignation"
                                        component={DocumentsUpload}
                                        idType={row.original.idType}
                                        designation={row.original.designation}
                                      />
                                      {!isSpecialRole && (
                                        <Field
                                          name="statutoryDeclaration"
                                          component={DocumentsUpload}
                                          idType={row.original.idType}
                                          designation={row.original.designation}
                                        />
                                      )}
                                    </>
                                  );
                                case "removal":
                                  return (
                                    <>
                                      {isAlternateDirector ? (
                                        <Field
                                          name="noticeOfRemoval"
                                          component={DocumentsUpload}
                                          idType={row.original.idType}
                                          designation={row.original.designation}
                                        />
                                      ) : (
                                        !isSpecialRole && (
                                          <Field
                                            name="shareholdersSpecialNotice"
                                            component={DocumentsUpload}
                                            idType={row.original.idType}
                                            designation={row.original.designation}
                                          />
                                        )
                                      )}
                                    </>
                                  );
                                case "death":
                                  return (
                                    <Field
                                      name="deathCertificate"
                                      component={DocumentsUpload}
                                      idType={row.original.idType}
                                      designation={row.original.designation}
                                    />
                                  );
                                case "bankruptcy":
                                  return (
                                    <Field
                                      name="insolvencyOrder"
                                      component={DocumentsUpload}
                                      idType={row.original.idType}
                                      designation={row.original.designation}
                                    />
                                  );
                                case "insanity":
                                  return (
                                    <Field
                                      name="medicalReport"
                                      component={DocumentsUpload}
                                      idType={row.original.idType}
                                      designation={row.original.designation}
                                    />
                                  );
                                case "courtOrder":
                                  return (
                                    <Field
                                      name="courtOrder"
                                      component={DocumentsUpload}
                                      idType={row.original.idType}
                                      designation={row.original.designation}
                                    />
                                  );
                                case "retirement":
                                case "rotation":
                                  return (
                                    <div className="p-4 bg-gray-50 rounded-md">
                                      <p className="text-gray-600">
                                        No document uploads are required for this
                                        type of cessation.
                                      </p>
                                    </div>
                                  );
                                default:
                                  return null;
                              }
                            })()}
                          </div>
                        )}
                        <div className="flex justify-between">
                          {!isFirstStep && (
                            <button
                              onClick={goToPrevStep}
                              className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
                              type="button"
                              disabled={isSubmitting}
                            >
                              <span className="flex items-center mr-4 h-7">
                                <ChevronLeftIcon
                                  className="flex-shrink-0 w-5 h-5"
                                  aria-hidden="true"
                                />
                              </span>
                              <p className="font-semibold">Go back</p>
                            </button>
                          )}
                          {isLastStep ||
                            (isRetirementOrRotation && isFirstStep) ? (
                            <Button
                              type="submit"
                              className="flex items-center ml-auto px-4 py-2 mb-2 font-semibold text-white rounded bg-sflPrimary mt-7 disabled:cursor-not-allowed"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <SpinnerIcon className="w-5 h-5 mr-2 text-white animate-spin" />
                                  <span>Submitting...</span>
                                </>
                              ) : (
                                "Submit"
                              )}
                            </Button>
                          ) : (
                            <Button
                              className="flex items-center ml-auto px-4 py-2 mb-2 font-semibold text-white rounded bg-sflPrimary mt-2 disabled:cursor-not-allowed"
                              type="submit"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <SpinnerIcon className="w-5 h-5 mr-2 text-white animate-spin" />
                                  <span>Submitting...</span>
                                </>
                              ) : (
                                "Save and Proceed"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                </Form>
              );
            }}
          </Formik>
        </td>
      </tr>
    </>
  );
};

export default CessationSubRowForDelete;
